import {
  type ComponentPropsWithoutRef,
  type ReactElement,
  type ReactNode,
} from 'react';

const LETTER_PATTERN = /[a-zA-Z]/;
const NON_DIGIT_PATTERN = /\D/g;
const NON_LETTER_PATTERN = /[^a-zA-Z]/g;
const DOT_OR_SPACE_PATTERN = /[.\s]+/g;

/** `… ext 5678` / `… ext. 5678` / `… x5678` at end of string only. */
const EXTENSION_SPLIT_PATTERN = /^(.*?)(?:\s+ext\.?\s+|\s+x)(\d+)\s*$/i;

const hasLetters = (value: string): boolean => LETTER_PATTERN.test(value);
const digitsOnly = (value: string): string =>
  value.replace(NON_DIGIT_PATTERN, '');

const LETTER_TO_DIGIT_MAP: Record<string, string> = Object.fromEntries(
  [
    ['abc', '2'],
    ['def', '3'],
    ['ghi', '4'],
    ['jkl', '5'],
    ['mno', '6'],
    ['pqrs', '7'],
    ['tuv', '8'],
    ['wxyz', '9'],
  ].flatMap(([letters, digit]) => [...letters].map((ch) => [ch, digit])),
);

/** Maps letters to digits; non-letters are dropped (e.g. `MD-MONTE` → `6366683`). */
const lettersToDigits = (value: string): string => {
  const sanitized = value.replace(NON_LETTER_PATTERN, '');

  return Array.from(sanitized).reduce((acc, char) => {
    const digit = LETTER_TO_DIGIT_MAP[char.toLowerCase()];
    if (digit) {
      return acc + digit;
    }

    return acc + char;
  }, '');
};

const formatDigitSegment = (digits: string): string => {
  if (digits.length >= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }

  if (digits.length === 5) {
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  }

  return digits;
};

/**
 * Finds the first letter: everything before it is the digit/punctuation prefix,
 * everything from that letter onward is the vanity letter segment.
 */
const splitVanityNumber = (
  number: string,
): { prefix: string; letters: string } | null => {
  const letterIdx = number.search(LETTER_PATTERN);
  if (letterIdx === -1) {
    return null;
  }

  const prefix = number.slice(0, letterIdx).trim();
  const letters = number.slice(letterIdx).trim();

  return { prefix, letters };
};

const formatVanityNumber = (
  number: string,
  phoneFormat: PhoneFormat,
): string | null => {
  const parts = splitVanityNumber(number);
  if (!parts) {
    return null;
  }

  let { prefix, letters } = parts;
  prefix = digitsOnly(prefix);
  if (prefix.length < 3) {
    return null;
  }

  const areaCode = prefix.slice(0, 3);
  const middleDigits = prefix.slice(3);

  if (phoneFormat === 'stripped') {
    const letterDigits = lettersToDigits(letters);
    const allDigits = prefix + letterDigits;
    const firstGroup = allDigits.slice(3, 6);
    const secondGroup = allDigits.slice(6);

    return `(${areaCode}) ${firstGroup}${secondGroup ? `-${secondGroup}` : ''}`;
  }

  letters = letters.replace(DOT_OR_SPACE_PATTERN, '-');
  const phoneNumber = middleDigits ? `${middleDigits}-${letters}` : letters;

  if (phoneFormat === 'abridged') {
    return `(${areaCode}) ${phoneNumber}`;
  }

  const lettersAsDigits = lettersToDigits(letters);
  if (lettersAsDigits.length < 1) {
    return null;
  }

  return `(${areaCode}) ${phoneNumber} (${formatDigitSegment(lettersAsDigits)})`;
};

/** Digits for vanity `tel:` (area code + letter keypad mapping). */
const vanityNumberToDigitsOnly = (number: string): string => {
  const parts = splitVanityNumber(number);
  if (!parts) {
    return digitsOnly(number);
  }

  return digitsOnly(parts.prefix) + lettersToDigits(parts.letters);
};

interface SplitPhoneExtension {
  main: string;
  extension: string | null;
}

const splitPhoneAndExtension = (phone: string): SplitPhoneExtension => {
  const matches = phone.match(EXTENSION_SPLIT_PATTERN);
  if (!matches) {
    return { main: phone.trim(), extension: null };
  }

  return { main: matches[1].trim(), extension: matches[2].trim() };
};

/**
 * Drops country code `+1` or `1-` prefix when present.
 */
const removeCountryCode = (phone: string): string => {
  if (phone.startsWith('+1') || phone.startsWith('1-')) {
    return phone
      .slice(2)
      .replace(/^[\s-]+/, '')
      .trimStart();
  }

  const allDigits = digitsOnly(phone);
  if (
    !hasLetters(phone) &&
    allDigits.length === 11 &&
    allDigits.startsWith('1')
  ) {
    return allDigits.slice(-10);
  }

  return phone;
};

const formatExtension = (extDigits: string): string => ` ext. ${extDigits}`;

const appendExt = (display: string, extDigits: string | null): string => {
  if (!extDigits) {
    return display;
  }

  return display + formatExtension(extDigits);
};

export interface FormatPhoneOptions {
  /** `abridged` shortens vanity display to `(area code) WORD` only; default `standard`. */
  format?: PhoneFormat;
}

/**
 * Formats a phone number string for display.
 *
 *  Input formats
 *  - ten-digit; no country code prefix (`+1` / `1`):
 *  - Digit-only numbers: `1234567890`, `123-456-7890`, `123 456 7890`
 *  - Vanity numbers: `800-MD-MONTE`
 *  - Numbers with extensions: `1234567890 ext 5678`, `1234567890 x5678`
 *
 *  Output formats:
 *  - Digit-only numbers: `(123) 456-7890`; if more than 10 digits, extra digits
 *    stay in the final segment (e.g. `(123) 456-789012`).
 *  - Vanity numbers: `(800) MD-MONTE (636-6683)`
 *  - Numbers with extensions: `(123) 456-7890 ext. 123`
 */
export const formatPhone = (
  phone: string,
  options?: FormatPhoneOptions,
): string => {
  const phoneFormat = options?.format ?? 'standard';
  const { main, extension } = splitPhoneAndExtension(phone);
  const trimmed = removeCountryCode(main);
  const effectiveVanityFormat =
    extension && hasLetters(trimmed) ? 'stripped' : phoneFormat;

  if (hasLetters(trimmed)) {
    const vanityNumber = formatVanityNumber(trimmed, effectiveVanityFormat);

    return appendExt(vanityNumber ?? trimmed, extension);
  }

  const digitString = digitsOnly(trimmed);
  if (digitString.length <= 0) {
    return '';
  }

  const areaCode = digitString.slice(0, 3);
  const firstGroup = digitString.slice(3, 6);
  const secondGroup = digitString.slice(6);
  const formatted = `(${areaCode}) ${firstGroup}${secondGroup ? `-${secondGroup}` : ''}`;

  return appendExt(formatted, extension);
};

/**
 * Dial path for `tel:` URIs.
 * Examples:
 *   - `123-456-7890` → `1234567890`
 *   - `800-MD-MONTE` → `8006366683`
 *   - `1234567890 ext 5678` → `1234567890;ext=5678`
 *   - `1234567890 x5678` → `1234567890;ext=5678`
 */
export const phoneToDialPath = (phone: string): string => {
  const { main, extension } = splitPhoneAndExtension(phone);
  const base = vanityNumberToDigitsOnly(removeCountryCode(main));

  return base + (extension ? `;ext=${extension}` : '');
};

/** Full `tel:` URI for anchors and analytics, or empty string when not dialable. */
export const phoneToTelUri = (phone: string): string => {
  const path = phoneToDialPath(phone);

  return path ? `tel:${path}` : '';
};

export type PhoneFormat = 'standard' | 'abridged' | 'stripped';
export interface PhoneNumberProps {
  phone: string;
  format?: PhoneFormat;
}

/*
 * Renders {@link formatPhone} output.
 */
export const PhoneNumber = ({
  phone,
  format = 'standard',
}: PhoneNumberProps): ReactElement => <>{formatPhone(phone, { format })}</>;

export interface PhoneLinkRenderArgs {
  /** Display text for the phone number (e.g. `(718) 555-2000`, `(800) MD-MONTE (636-6683)`). */
  formattedPhone: string;
  /** Full `tel:` URI for the anchor `href` (e.g. `tel:7185552000;ext=99`). */
  formattedHref: string;
}

export type PhoneLinkProps = Omit<
  ComponentPropsWithoutRef<'a'>,
  'href' | 'children'
> & {
  phone: string;
  format?: PhoneFormat;
  children?: (args: PhoneLinkRenderArgs) => ReactNode;
};

const defaultPhoneLinkChildren = ({
  formattedPhone,
}: PhoneLinkRenderArgs): ReactNode => formattedPhone;

/**
 * Anchor with `href` from {@link phoneToTelUri}. Pass `children` as a function
 * receiving {@link PhoneLinkRenderArgs} for display text and dial URI.
 * Leading and trailing whitespace on {@link phone} is ignored.
 */
export const PhoneLink = ({
  phone,
  children = defaultPhoneLinkChildren,
  rel,
  format = 'standard',
  ...rest
}: PhoneLinkProps): ReactElement | null => {
  const formattedHref = phoneToTelUri(phone);
  const formattedPhone = formatPhone(phone, { format });

  if (!formattedHref) {
    return <>{formattedPhone || phone}</>;
  }

  return (
    <a href={formattedHref} rel={rel ?? 'noopener noreferrer'} {...rest}>
      {children({ formattedPhone, formattedHref })}
    </a>
  );
};
